import { DOCUMENT } from '@angular/common';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideClientHydration } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';

import { CHAT_BACKEND } from './shared/chat/chat-backend';
import { ApiChatBackendService } from './shared/chat/api-chat-backend.service';
import { MockChatBackendService } from './shared/chat/mock-chat-backend.service';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authRefreshInterceptor } from './shared/auth/auth-refresh.interceptor';
import { backendUrlInterceptor } from './shared/config/backend-url.interceptor';
import { timeoutInterceptor } from './shared/config/timeout.interceptor';
import { PaymentRequiredService } from './shared/payments/payment-required.service';
import { GlobalErrorHandler } from './shared/config/global-error-handler';
import { UtmTrackingService } from './shared/tracking/utm-tracking.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Order matters: authRefreshInterceptor should see the original relative URLs.
    // backendUrlInterceptor can then rewrite to absolute URLs for staging/prod.
    provideHttpClient(
      withFetch(),
      withInterceptors([authRefreshInterceptor, backendUrlInterceptor, timeoutInterceptor]),
    ),
    {
      provide: CHAT_BACKEND,
      useFactory: () => {
        const doc = inject(DOCUMENT);
        const w = doc.defaultView;
        const search = w?.location?.search ?? '';
        const params = new URLSearchParams(search);
        const fromQuery = params.get('data'); // `api` | `mock`
        const fromStorage = w?.localStorage?.getItem('pc-Chat.dataSource');
        const mode = environment.production
          ? 'api'
          : (fromQuery ?? fromStorage ?? 'api').toLowerCase();

        if (!environment.production && fromQuery && w?.localStorage) {
          w.localStorage.setItem('pc-Chat.dataSource', mode);
        }

        if (mode === 'api') return inject(ApiChatBackendService);
        return inject(MockChatBackendService);
      },
    },
    provideEnvironmentInitializer(() => inject(UtmTrackingService)),
    provideClientHydration(),
    provideRouter(
      routes,
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: ({ transition }) => {
          if (inject(PaymentRequiredService).isOpen()) transition.skipTransition();
        },
      }),
    ),
  ],
};
